import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { HOST_MB } from '@/lib/constants';
import { useContractsData } from '@/hooks/api/use-contracts';
import usePageTitle from '@/hooks/use-page-title';
import useBrandName from '@/hooks/use-brand-name';
import { hasContractApporteur43397, hasContractVAEUnder14Days as hasVAEUnder14Days, hasOtherContract as hasOther } from '@/lib/contracts';

const PlanDuSite = () => {
    usePageTitle('Plan du site');
    const brandName = useBrandName();
    const contracts = useContractsData();
    const hasApporteur43397 = hasContractApporteur43397(contracts);
    const hasContractVAEUnder14Days = hasVAEUnder14Days(contracts);
    const hasOtherContract = hasOther(contracts);

    const sections = [
        {
            title: 'Mon espace',
            links: [
                { to: '/', label: 'Accueil', show: true },
                { to: '/mes-contrats', label: 'Mes contrats', show: true },
                { to: '/mes-paiements', label: 'Mes paiements', show: true },
                { to: '/mes-sinistres', label: 'Mes sinistres', show: true },
                { to: '/mes-documents', label: 'Mes documents', show: true },
                { to: '/mes-questions', label: 'Mes questions / FAQ', show: true },
                { to: '/mes-avantages', label: 'Mes avantages', show: HOST_MB === 43397 && hasOtherContract },
                { to: '/mes-parrainages', label: 'Mes parrainages', show: HOST_MB === 43397 && hasApporteur43397 && hasOtherContract },
            ],
        },
        {
            title: 'Mon profil',
            links: [
                { to: '/mon-profil', label: 'Mon profil', show: true },
                { to: '/mon-profil/je-change-mes-informations-bancaires', label: 'Modifier mes informations bancaires', show: true },
            ],
        },
        {
            title: 'Aide et contact',
            links: [
                { to: '/contact', label: 'Nous contacter', show: HOST_MB === 43397 },
                { to: '/assistance', label: 'Assistance', show: true },
                { to: '/resilier', label: hasContractVAEUnder14Days ? 'Renoncer mon contrat' : 'Résilier mon contrat', show: HOST_MB === 43397 && hasApporteur43397 },
            ],
        },
        {
            title: 'Informations légales',
            links: [
                { to: '/mentions-legales', label: 'Mentions légales', show: true },
                { to: '/gestion-des-cookies', label: 'Gestion des cookies', show: true },
                { to: '/plan-du-site', label: 'Plan du site', show: true },
            ],
        },
    ];

    return (
        <div className="space-y-6">
            <div className="space-y-2 sm:space-y-3 mt-4">
                <nav className="flex items-center gap-1.5 text-xs sm:text-sm text-muted-foreground" aria-label="Fil d'Ariane">
                    <Link to="/" className="hover:text-foreground transition-colors no-underline">Mon espace</Link>
                    <ChevronRight className="w-3.5 h-3.5" aria-hidden="true" />
                    <span>Plan du site</span>
                </nav>
                <h1 className="text-xl sm:text-2xl font-bold">Plan du site</h1>
                <p className="text-sm text-muted-foreground">
                    Retrouvez l'ensemble des pages disponibles sur votre espace assuré {brandName}.
                </p>
            </div>

            <nav aria-label="Plan du site" className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {sections.map((section) => {
                    const visibleLinks = section.links.filter((l) => l.show);
                    if (visibleLinks.length === 0) return null;
                    return (
                        <Card key={section.title}>
                            <CardHeader className="pb-6 border-b">
                                <CardTitle className="text-lg">{section.title}</CardTitle>
                            </CardHeader>
                            <CardContent className="pt-6">
                                <ul className="space-y-2">
                                    {visibleLinks.map((link) => (
                                        <li key={link.to}>
                                            <Link
                                                to={link.to}
                                                className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
                                            >
                                                <ChevronRight className="w-4 h-4" aria-hidden="true" />
                                                {link.label}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </CardContent>
                        </Card>
                    );
                })}
            </nav>
        </div>
    );
};

export default PlanDuSite;
